import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {isTeammateNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {LensModel} from '../../../../models/api/lensModel';
import {NavigationLineChevron} from '../../../common/navigation/navigationLineChevron';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {WorkspacesEditorRarelyViewedBadge} from '../editor/workspacesEditorRarelyViewedBadge';
import {renderHighlight} from '../iconOrEmojiRenderer';
import {WorkspaceLineDropTarget, WorkspaceLineDropTargetProps} from '../line/workspaceLineDropTarget';
import {WorkspaceLineMore, WorkspaceLineMoreProps} from '../line/workspaceLineMore';
import {StyledLabelDiv, StyledLockDiv} from '../workspaceCustomViewItem';
import {WorkspaceItemRefinedResultsIndicator} from '../workspaceItemRefinedResultsIndicator';
import {LensValidation, useLensValidation} from './helpers/useLensValidation';
import {LensesWorkspaceTooltipCoordinator} from './tooltip/lensesWorkspaceTooltipCoordinator';

/*
 * Props.
 */

export interface LensesWorkspaceItemProps
  extends WorkspaceLineDropTargetProps,
    Omit<WorkspaceLineMoreProps, 'isGuidedDemoActive'> {
  /** The Lens. */
  lens: LensModel;
  /** Should the lens name be bold. */
  isUnread?: boolean;
  /** Whether the current item is currently expanded. */
  isExpanded?: boolean;
  /** Handler to collapse/expand this item. */
  onToggleExpandClick?: () => void;
  /** Should the checkbox be selected if we're in editing mode. */
  isChecked?: boolean;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  isRecentlyViewed?: boolean;
  shouldShowRefinedResultsIndicator: boolean;
}

const StyledTextDiv = styled.div`
  flex-shrink: 1;
  min-width: 0;
  ${ellipsis()};
`;

const StyledWorkspaceItemRefinedResultsIndicator = styled(WorkspaceItemRefinedResultsIndicator)`
  margin-left: 4px;
`;

/*
 * Component.
 */

/** Display a lens as a P1 workspace line. */
export const LensesWorkspaceItem: FC<LensesWorkspaceItemProps> = (props) => {
  const {lens, isSelected, shouldShowRefinedResultsIndicator} = props;
  const theme = useUserTheme();

  const {validateLens} = useLensValidation();
  const lensValidation = useMemo(() => validateLens(lens), [lens, validateLens]);

  return (
    <WorkspaceLineDropTarget
      {...props}
      shouldHidePreviewBadge={props.isEditingWorkspace}
      interactionId={`workspace-lens-${lens.id}`}
      isDisabled={lensValidation.isLensInvalid}
      isOpaque
      ignoreDisabledPointerStyling
    >
      <LensesWorkspaceTooltipCoordinator
        lens={lens}
        lensValidation={lensValidation}
        isEditingWorkspace={props.isEditingWorkspace}
      >
        {maybeRenderChevron(props, lensValidation)}
        {renderHighlight(
          lens,
          isSelected,
          !props.isEditingWorkspace && lensValidation.isLensInvalid,
          'savedViewSmall',
          'savedViewFilledSmall',
        )}
        <NavigationLineLabel
          isTransparent={!props.isEditingWorkspace && lensValidation.isLensInvalid}
          isUnread={props.isUnread}
          isEditingWorkspace={props.isEditingWorkspace}
          shouldIgnoreTooltipCondition
        >
          <StyledLabelDiv>
            <StyledTextDiv>{lens.name}</StyledTextDiv>
            {maybeRenderLock(lens, theme)}
            {shouldShowRefinedResultsIndicator && <StyledWorkspaceItemRefinedResultsIndicator />}
            {props.isRecentlyViewed === false && <WorkspacesEditorRarelyViewedBadge />}
          </StyledLabelDiv>
        </NavigationLineLabel>
      </LensesWorkspaceTooltipCoordinator>
      {renderRightItem(props, lensValidation, theme)}
    </WorkspaceLineDropTarget>
  );
};

/*
 * Helpers.
 */

function maybeRenderChevron(props: LensesWorkspaceItemProps, lensValidation: LensValidation) {
  const {isEditingWorkspace, onToggleExpandClick} = props;
  if (!onToggleExpandClick) {
    return null;
  }

  return (
    <NavigationLineChevron
      {...props}
      onToggleExpandClick={onToggleExpandClick}
      isTransparent={!isEditingWorkspace && lensValidation.isLensInvalid}
    />
  );
}

export function maybeRenderLock(lens: LensModel, theme: UserTheme) {
  // Only render the lock for private lens.
  if (!isTeammateNamespace(lens.namespace)) {
    return null;
  }

  return (
    <StyledLockDiv>
      <Icon name="lockFilledTiny" color={theme.itemPrivateLock} />
    </StyledLockDiv>
  );
}

function renderRightItem(props: LensesWorkspaceItemProps, lensValidation: LensValidation, theme: UserTheme) {
  if (lensValidation.isLensInvalid && !props.isEditingWorkspace) {
    return (
      <LensesWorkspaceTooltipCoordinator lens={props.lens} lensValidation={lensValidation} rightItemMode>
        <Icon name="warningFilledSmall" color={theme.palette.orange.shade40} />
      </LensesWorkspaceTooltipCoordinator>
    );
  }

  return (
    <WorkspaceLineMore
      renderMenu={props.renderMenu}
      badgeValue={props.badgeValue}
      isUnread={props.isUnread}
      loadingType={props.loadingType}
      shouldShowOfflineIcon={props.shouldShowOfflineIcon}
      isEditingWorkspace={props.isEditingWorkspace}
      isChecked={props.isChecked}
      shouldRemoveBadgeLeftSpacing={props.isRecentlyViewed === false}
    />
  );
}
